import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useUser } from '../../context/UserContext';
import { useChat } from './ChatContext';
import { uniqueChannelName } from '../../lib/realtime';

export interface ChatChannel {
  id: string;
  name: string;
  type: 'general' | 'client' | 'direct';
  client_id: string | null;
  description: string | null;
  created_by: string | null;
  agency_id: string;
  archived: boolean;
  created_at: string;
  updated_at: string;
  unread_count: number;
  last_message_at: string | null;
  last_message_preview: string | null;
  last_read_at: string | null;
}

export function useChatChannels() {
  const { user } = useUser();
  const { refreshKey } = useChat();
  const [channels, setChannels] = useState<ChatChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const channelsRef = useRef<ChatChannel[]>([]);

  // Keep ref in sync for use in subscription handler
  useEffect(() => {
    channelsRef.current = channels;
  }, [channels]);

  const sortChannels = (chs: ChatChannel[]): ChatChannel[] => {
    return [...chs].sort((a, b) => {
      if (a.unread_count > 0 && b.unread_count === 0) return -1;
      if (a.unread_count === 0 && b.unread_count > 0) return 1;
      const aTime = a.last_message_at || a.created_at;
      const bTime = b.last_message_at || b.created_at;
      return new Date(bTime).getTime() - new Date(aTime).getTime();
    });
  };

  const fetchChannels = useCallback(async () => {
    if (!user) return;

    try {
      // 1. Get memberships
      const { data: memberships, error: memberError } = await supabase
        .from('chat_channel_members')
        .select('channel_id, last_read_at')
        .eq('user_id', user.id);

      if (memberError) throw memberError;
      if (!memberships || memberships.length === 0) {
        setChannels([]);
        setTotalUnreadCount(0);
        setLoading(false);
        return;
      }

      const channelIds = memberships.map(m => m.channel_id);
      const lastReadMap = new Map(
        memberships.map(m => [m.channel_id, m.last_read_at])
      );

      // 2. Fetch channel details (single query)
      const { data: channelData, error: channelError } = await supabase
        .from('chat_channels')
        .select('*')
        .in('id', channelIds)
        .eq('archived', false)
        .order('updated_at', { ascending: false });

      if (channelError) throw channelError;
      if (!channelData || channelData.length === 0) {
        setChannels([]);
        setTotalUnreadCount(0);
        setLoading(false);
        return;
      }

      // 3. Fetch recent messages per channel in a single batch (capped for performance)
      const { data: recentMessages } = await supabase
        .from('chat_messages')
        .select('channel_id, content, created_at')
        .in('channel_id', channelIds)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(500);

      // Build map of last message per channel
      const lastMessageMap = new Map<string, { content: string; created_at: string }>();
      for (const msg of recentMessages || []) {
        if (!lastMessageMap.has(msg.channel_id)) {
          lastMessageMap.set(msg.channel_id, {
            content: msg.content,
            created_at: msg.created_at,
          });
        }
      }

      // 4. Compute unread counts — batch by counting messages after last_read_at
      //    We already have all messages, so count from there
      const unreadMap = new Map<string, number>();
      for (const chId of channelIds) {
        const lastReadAt = lastReadMap.get(chId);
        if (!lastReadAt) {
          // Never read — count all messages in this channel
          const count = (recentMessages || []).filter(m => m.channel_id === chId).length;
          unreadMap.set(chId, count);
        } else {
          const lastReadDate = new Date(lastReadAt).getTime();
          const count = (recentMessages || []).filter(
            m => m.channel_id === chId && new Date(m.created_at).getTime() > lastReadDate
          ).length;
          unreadMap.set(chId, count);
        }
      }

      // 5. Build enriched channels
      const enrichedChannels: ChatChannel[] = channelData.map(channel => {
        const lastMsg = lastMessageMap.get(channel.id);
        return {
          ...channel,
          type: channel.type as ChatChannel['type'],
          unread_count: unreadMap.get(channel.id) || 0,
          last_message_at: lastMsg?.created_at || null,
          last_message_preview: lastMsg?.content
            ? lastMsg.content.substring(0, 80)
            : null,
          last_read_at: lastReadMap.get(channel.id) || null,
        };
      });

      const sorted = sortChannels(enrichedChannels);
      setChannels(sorted);
      setTotalUnreadCount(sorted.reduce((sum, ch) => sum + ch.unread_count, 0));
    } catch (err) {
      console.error('Error fetching chat channels:', err);
      setError((err as Error).message || 'Failed to load channels');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchChannels();
  }, [fetchChannels, refreshKey]);

  // Listen for new messages — update just the affected channel instead of full refetch
  useEffect(() => {
    if (!user) return;

    // Debounce full refetch to avoid storms
    let refetchTimer: ReturnType<typeof setTimeout> | null = null;

    const subscription = supabase
      .channel(uniqueChannelName('chat_channels_global'))
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'chat_messages' },
        (payload) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const newMsg = payload.new as any;
          const channelId = newMsg.channel_id;
          const isOwnMessage = newMsg.user_id === user.id;

          // Quick local update: bump the affected channel's last message + unread count
          setChannels(prev => {
            const updated = prev.map(ch => {
              if (ch.id !== channelId) return ch;
              return {
                ...ch,
                last_message_at: newMsg.created_at,
                last_message_preview: (newMsg.content || '').substring(0, 80),
                updated_at: newMsg.created_at,
                // Don't increment unread for own messages
                unread_count: isOwnMessage ? ch.unread_count : ch.unread_count + 1,
              };
            });
            const sorted = sortChannels(updated);
            setTotalUnreadCount(sorted.reduce((sum, ch) => sum + ch.unread_count, 0));
            return sorted;
          });

          // Debounced full refetch to sync accurate counts (every 10s max)
          if (refetchTimer) clearTimeout(refetchTimer);
          refetchTimer = setTimeout(() => {
            fetchChannels();
          }, 10000);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
      if (refetchTimer) clearTimeout(refetchTimer);
    };
  }, [user, fetchChannels]);

  return { channels, loading, error, totalUnreadCount, refetch: fetchChannels };
}
